import {
  DataGrid,
  GridColDef,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import {
  differenceBetweenDateStringsInDays,
  hasDatePassed,
  truncateISOString,
} from '../../utils/dateTime';
import { HardwareItemFilter } from './hardwareTypes';
import { CircularProgress } from '@mui/material';
import { Item } from '../../utils/sharedTypes';
import { Order } from '../../api/api-generated';
import { useFindAllHardwareQuery } from '../../api/api-hooks';
import { filterHardwareItem } from './hardwareUtils';
import { useGridSort } from '../../hooks/useGridSort';

interface Props {
  setSelectedHardware: React.Dispatch<React.SetStateAction<Item[]>>;
  filters?: HardwareItemFilter;
  order?: Order;
  excludeOrder?: boolean;
  lptSold: boolean;
  setEditModalOpenFlag?: React.Dispatch<React.SetStateAction<boolean>>;
}

const HardwareTable = ({
  setSelectedHardware,
  filters,
  lptSold,
  order,
  excludeOrder = false,
  setEditModalOpenFlag,
}: Props) => {
  const { data: hardware } = useFindAllHardwareQuery({});
  const { sortModel, handleSortModelChange } = useGridSort('id', 'desc');
  if (hardware === undefined) return <CircularProgress />;
  // Define data for display in DataGrid
  const columns: GridColDef[] = [
    { field: 'id', hide: true },
    { field: 'companyName', headerName: 'Company', width: 150 },
    { field: 'analyzerType', headerName: 'Hardware Type', width: 180 },
    { field: 'serial', headerName: 'Serial', width: 150 },
    { field: 'firmware', headerName: 'Firmware', width: 150 },
    { field: 'soldDate', headerName: 'Sold Date', width: 150 },
    { field: 'warrantyStartDate', headerName: 'Warranty Start', width: 170 },
    { field: 'warrantyDuration', headerName: 'Warranty Length', width: 190 },
    { field: 'warrantyStatus', headerName: 'Warranty Status', width: 180 },
    { field: 'trackingNumber', headerName: 'Tracking #', width: 150 },
    { field: 'shipper', headerName: 'Shipper', width: 150 },
  ];
  const rows = hardware
    .filter((item) => {
      if (lptSold && !item.lptSold) return false;
      // If we have an order prop and we've found the order...
      if (order && Number(order.id) === Number(item.orderId) && excludeOrder)
        return false;
      // If we have an order prop and we haven't found the order...
      if (order && Number(order.id) !== Number(item.orderId) && !excludeOrder)
        return false;
      if (
        order &&
        item.companies.length !== 0 &&
        Number(order.companyId) !== Number(item.companies[0].id)
      )
        return false;
      return !(filters && !filterHardwareItem(item, filters));
    })
    .map((item) => ({
      ...item,
      companyName:
        item.companies.length === 0
          ? 'None'
          : item.companies.length > 1
          ? 'Multiple'
          : item.companies[0].companyName,
      analyzerType: item.hardwareType.typeName,
      soldDate:
        item.order === null
          ? item.warrantyStartDate
            ? truncateISOString(item.warrantyStartDate)
            : ''
          : truncateISOString(item.order.orderDate),
      warrantyStartDate:
        item.warrantyStartDate === null
          ? 'None'
          : truncateISOString(item.warrantyStartDate),
      warrantyDuration:
        item.warrantyEndDate === null || item.warrantyStartDate === null
          ? 'None'
          : differenceBetweenDateStringsInDays(
              item.warrantyEndDate,
              item.warrantyStartDate
            ) + ' Days',
      warrantyStatus:
        item.warrantyEndDate === null
          ? 'None'
          : hasDatePassed(new Date(item.warrantyEndDate))
          ? 'Expired'
          : 'Active',
    }));
  const handleTableSelection = (model: GridSelectionModel) => {
    setSelectedHardware(
      hardware.filter((hardware) => model.includes(hardware.id))
    );
  };
  const handleRowClick = (params: GridRowParams) => {
    if (setEditModalOpenFlag) {
      setSelectedHardware(hardware.filter((item) => item.id === params.row.id));
      setEditModalOpenFlag(true);
    }
  };
  return (
    <div style={{ paddingBottom: '1rem', width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSize={100}
        checkboxSelection
        disableSelectionOnClick
        autoHeight
        sortModel={sortModel}
        disableColumnMenu={true}
        onSelectionModelChange={handleTableSelection}
        onSortModelChange={handleSortModelChange}
        onRowClick={handleRowClick}
      />
    </div>
  );
};

export default HardwareTable;
